// MindForge web — TypeScript port of logic/spatial.py.
//
// Grid coordinates use named objects { row, col } instead of bare tuples for
// readability and parity with the Swift port. Equality on positions is
// structural — see positionEquals.

export interface Position {
  row: number;
  col: number;
}

export interface SpatialScore {
  correctPositions: number;
  total: number;
  accuracy: number;
}

function positionEquals(a: Position, b: Position): boolean {
  return a.row === b.row && a.col === b.col;
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Generate a sequence of grid positions with no immediate repeats.
 * Caps automatically at gridSize×gridSize positions (can't generate more
 * than the grid contains without repeats — degenerate case allows repeats).
 */
export function generateSpatialSequence(
  gridSize: number,
  length: number,
): Position[] {
  if (length <= 0 || gridSize <= 0) return [];

  const allPositions: Position[] = [];
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      allPositions.push({ row: r, col: c });
    }
  }
  const cappedLength = Math.min(length, allPositions.length);

  const seq: Position[] = [];
  while (seq.length < cappedLength) {
    const last = seq[seq.length - 1];
    let candidates: Position[];
    if (last !== undefined) {
      candidates = allPositions.filter((p) => !positionEquals(p, last));
    } else {
      candidates = allPositions;
    }
    if (candidates.length === 0) candidates = allPositions;
    seq.push(randChoice(candidates));
  }
  return seq;
}

/** Score a spatial recall attempt. */
export function scoreSpatial(
  playerClicks: Position[],
  expected: Position[],
): SpatialScore {
  const n = Math.min(playerClicks.length, expected.length);
  let correct = 0;
  for (let i = 0; i < n; i++) {
    if (positionEquals(playerClicks[i], expected[i])) correct += 1;
  }
  const total = expected.length;
  const accuracy = correct / Math.max(1, total);
  return {
    correctPositions: correct,
    total,
    accuracy: total === 0 ? 0 : accuracy,
  };
}

/** Grid dimension based on difficulty level. */
export function gridSizeForDifficulty(difficulty: number): number {
  if (difficulty >= 10) return 6;
  if (difficulty >= 7) return 5;
  return 4;
}

/** Number of cells to highlight based on difficulty. */
export function sequenceLengthForDifficulty(difficulty: number): number {
  return Math.min(2 + difficulty, 12);
}
